import {EventEmitter, Injectable} from "@angular/core";
import {
  Applicant,
  ApplicantAddress,
  ApplicantRequest,
  ApplicationDetailLog,
  ApplicationLogsRequest
} from "leia-api";
import {AnnualIncome} from "src/app/shared/model/annual-income";
import {Country} from "src/app/shared/model/country";
import {IncomeSource} from "src/app/shared/model/income-source";
import {Occupation} from "src/app/shared/model/occupation";
import {OnboardingApplicant} from "src/app/shared/model/onboarding-applicant.model";
import {OnboardingTellUsAboutYourself} from "src/app/shared/model/onboarding-tell-us-about-yourself.model";
import {FacadeService} from "src/app/shared/service-facade/facade.service";
import {ApplicantType} from "src/app/signup/models/applicant-type";
import StatusEnum = ApplicationLogsRequest.StatusEnum;
import {Router} from "@angular/router";
import {UtilitiesService} from "../../shared/services/utilities.service";
import {DeviceDetectorService} from "ngx-device-detector";
import { BehaviorSubject } from "rxjs";

@Injectable({
  providedIn: "root"
})
export class ReviewService {
  public termsConditionsAcceptanceProcessComplete = new EventEmitter<void>();
  private _formInEditMode = new BehaviorSubject<boolean>(false);
  formInEditMode$ = this._formInEditMode.asObservable();
  public editStateMapChanged = new EventEmitter<void>();

  public editStateArray: Array<[string, number]> = [];


  constructor(
    private _facade: FacadeService,
    private router: Router,
    private utils: UtilitiesService,
    private deviceDetectorService: DeviceDetectorService
  ) {
    this.editStateMapChanged.subscribe(() => {
      this._formInEditMode.next(this.editStateArray.length > 0);
    });
  }

  setApplicationData(
    data: ApplicationDetailLog,
    annualIncomes: AnnualIncome[],
    incomeSources: IncomeSource[],
    occupations: Occupation[],
    countries: Country[],
    brandId: number,
    isForJointMaintenance: boolean,
    isInAuthenticatedFlow: boolean
  ): void {

    let applicationLogRequest = this._facade.getShoppingCartApplicationLogsRequest();
    let onboardingRequest = this._facade.getShoppingCartOnboardingRequest();
    const {
      products,
      isJoint,
      applicationType,
      costCenter,
      officerCode,
      hasConsentDisclosure,
      applicationChannelId,
      rcCode,
      branchId,
      url,
      journeyToken
    } = data;
    
    if (data.applicants!.length > 1) {
      this._facade.markApplicationAsJoint();
    }

    applicationLogRequest.applicants = [];
    onboardingRequest.applicants = [];

    data.applicants?.sort((a, b) => Number(b.primary) - Number(a.primary));

    data.applicants?.forEach(applicant => {
      let {
        applicantId, annualIncome, employerCountry, occupation, primarySourceOfFunds,
        firstName, middleName, lastName, suffix, nickName, ssn, dateOfBirth, email, phoneNumber,
        primary, isKyc, cif, isEnrolledOLB, isExistingCustomer, isInGoodStanding,
        returningUser, isUSCitizen, citizenship
      } = applicant as any;

      let streetAddress = "", streetAddress1 = "", city  = "", zip = "", state = "", addressType = 1;

      if (applicant.addresses?.length) {
        const address = applicant.addresses![0];
        streetAddress = address.streetAddress!;
        streetAddress1 = address.streetAddress1!;
        city = address.city!;
        zip = address.zip!;
        state = address.state!;
        addressType = address.addressType!;
      }

      const dateOfBirthWithTime = UtilitiesService.addTimeToDateString(dateOfBirth);

      if (!isForJointMaintenance && !isInAuthenticatedFlow) {
        const annualIncomeId = this._getCatalogId("annualIncomeId", annualIncome!, annualIncomes);
        const incomeSourceId = this._getCatalogId("incomeSourceId", primarySourceOfFunds!, incomeSources);
        const occupationId = this._getCatalogId("occupationId", occupation!, occupations);
        const countryId = this._getCatalogId("countryId", employerCountry!, countries);

        onboardingRequest.applicants.push(
          this._buildOnboardingApplicant(
            applicantId, cif, primary, firstName, middleName, lastName, suffix, nickName, dateOfBirthWithTime, ssn, email,
            phoneNumber, addressType, streetAddress, streetAddress1, city, state, zip, isKyc, isEnrolledOLB,
            isExistingCustomer, isInGoodStanding, returningUser, annualIncomeId, incomeSourceId,
            occupationId, countryId
          )
        );

        applicationLogRequest.applicants?.push(
          this._buildApplicantRequest(
            applicantId, cif, primary, firstName, middleName, lastName, suffix, nickName, dateOfBirthWithTime, ssn, email,
            phoneNumber, addressType, streetAddress, streetAddress1, city, state, zip, isKyc, isEnrolledOLB,
            isExistingCustomer, isInGoodStanding, returningUser, annualIncomeId.toString(),
            incomeSourceId.toString(), occupationId.toString(), countryId.toString(), isUSCitizen, citizenship
          )
        );
      } else {
        onboardingRequest.applicants.push(
          this._buildOnboardingApplicant(
            applicantId, cif, primary, firstName, middleName, lastName, suffix, nickName, dateOfBirthWithTime, ssn, email,
            phoneNumber, addressType, streetAddress, streetAddress1, city, state, zip, isKyc, isEnrolledOLB,
            isExistingCustomer, isInGoodStanding, returningUser
          )
        );

        applicationLogRequest.applicants?.push(
          this._buildApplicantRequest(
            applicantId, cif, primary, firstName, middleName, lastName, suffix, nickName, dateOfBirthWithTime, ssn, email,
            phoneNumber, addressType, streetAddress, streetAddress1, city, state, zip, isKyc, isEnrolledOLB,
            isExistingCustomer, isInGoodStanding, returningUser, undefined, undefined, undefined, undefined,
            isUSCitizen, citizenship
          )
        );
      }
    });


    onboardingRequest.copyAddressFromPrimary = this._isAddressSharedBetweenApplicants(data.applicants!);

    const { usingChecks, usingDebitCard, achTransactions, cashTransactions, wireTransferDomestic, wireTransferInternational} = products![0];
    const useChecks = usingChecks, useDebitCard = usingDebitCard;
    onboardingRequest.bsaInfo = {
      ...onboardingRequest.bsaInfo,
      achTransactions,
      cashTransactions,
      wireTransferDomestic,
      wireTransferInternational,
      useChecks,
      useDebitCard
    };

    applicationLogRequest = {
      ...applicationLogRequest,
      products,
      isJoint,
      applicationType,
      costCenter,
      officerCode,
      hasConsentDisclosure,
      applicationChannelId,
      rcCode,
      branchId,
      url,
      journeyToken
    };

    applicationLogRequest.status = StatusEnum.Started;

    this._facade.handleUserAgentProperties(applicationLogRequest, "Application Verified. Browser Details: " + JSON.stringify(this.deviceDetectorService.getDeviceInfo()));

    this._facade.setShoppingCartApplicationLogsRequest(applicationLogRequest);
    this._facade.setShoppingCartOnboardingRequest(onboardingRequest);
    this._facade.pushShoppingCartOnboardingRequest(onboardingRequest);
  }

  private _isAddressSharedBetweenApplicants(applicants: Applicant[]): boolean {
    if (!applicants || applicants.length === 1) {
      return false;
    }

    const serializedAddresses: string[] = [];
    applicants.forEach(applicant => serializedAddresses.push(JSON.stringify(applicant.addresses)));

    return new Set(serializedAddresses).size === 1;
  }

  private _getCatalogId(idKey: string, value: string, catalog: any[]): number {
    try {
      if (!value) {
        console.error("BB8 error mapping catalog data, value empty", idKey, value, catalog);
        return -1;
      }

      const providedId = value.split("|")[0];
      const idRecord = catalog.find(record => record[idKey].toString() === providedId);

      if (!idRecord) {
        console.error("BB8 error mapping catalog data, record not found", idKey, value, catalog);
        return -1;
      }

      return idRecord[idKey];

    } catch (error) {
      console.error("BB8 error mapping catalog data", idKey, value, catalog);
      return -1;
    }
  }

  private _buildOnboardingApplicant(
    applicantId: number = -1,
    cif: string = "",
    primary: boolean = false,
    firstName: string = "",
    middleName: string = "",
    lastName: string = "",
    suffix: string = "",
    preferredName: string = "",
    birthDate: string = "",
    ssnNumber: string = "",
    email: string = "",
    phoneNumber: string = "",
    addressType: number = -1,
    addressLine1: string = "",
    addressLine2: string = "",
    city: string = "",
    state: string = "",
    zipcode: string = "",
    requiresKyc: boolean = false,
    isEnrolledInOLB: boolean = false,
    isExisting: boolean = false,
    isInGoodStanding: boolean = false,
    isReturningUser: boolean = false,
    annualIncomeId: number = -1,
    sourceOfIncomeId: number = -1,
    occupationId: number = -1,
    countryId: number = -1,
    hasInfoMismatch = false
  ): OnboardingApplicant {
    let onboardingApplicant = new OnboardingApplicant();

    onboardingApplicant = {
      ...onboardingApplicant,
      applicantId,
      cif
    };

    onboardingApplicant.applicantType = primary ? ApplicantType.Primary : ApplicantType.Secondary;

    let tellUsAboutYourself = new OnboardingTellUsAboutYourself();

    tellUsAboutYourself = {
      ...tellUsAboutYourself,
      firstName,
      middleName,
      lastName,
      suffix,
      preferredName,
      ssnNumber,
      email,
      phoneNumber,
      addressType,
      addressLine1,
      addressLine2,
      city,
      state,
      zipcode
    };

    if (birthDate) {
      tellUsAboutYourself.birthDate = new Date(birthDate);
    }

    onboardingApplicant.tellUsAboutYourself = tellUsAboutYourself;
    onboardingApplicant.customerInfo = {
      ...onboardingApplicant.customerInfo,
      requiresKyc,
      isEnrolledInOLB,
      isExisting,
      isInGoodStanding,
      isReturningUser,
      hasInfoMismatch
    };

    onboardingApplicant.employmentDetails = {
      ...onboardingApplicant.employmentDetails,
      annualIncomeId,
      sourceOfIncomeId,
      occupationId,
      countryId
    };

    return onboardingApplicant;
  }

  private _buildApplicantRequest(
    applicantId: number = -1,
    cif: string = "",
    primary: boolean = false,
    firstName: string = "",
    middleName: string = "",
    lastName: string = "",
    suffix: string = "",
    preferredName: string = "",
    dateOfBirth: string = "",
    ssn: string = "",
    email: string = "",
    phoneNumber: string = "",
    addressType: number = -1,
    streetAddress: string = "",
    streetAddress1: string = "",
    city: string = "",
    stateAbbrev: string = "",
    zip: string = "",
    isKyc: boolean = false,
    isEnrolledOLB: boolean = false,
    isExistingCustomer: boolean = false,
    isInGoodStanding: boolean = false,
    returningUser: boolean = false,
    annualIncome: string = "",
    primarySourceOfFunds: string = "",
    occupation: string = "",
    employerCountry: string = "",
    isUSCitizen?: boolean,
    citizenship?: string
  ): ApplicantRequest {
    let applicantRequest = new ApplicantRequest();
    const address = new ApplicantAddress();
    const addresses = [];

    address.addressType = addressType;
    address.streetAddress = streetAddress;
    address.streetAddress1 = streetAddress1;
    address.city = city;
    address.state = stateAbbrev;
    address.zip = zip;

    addresses.push(address);

    applicantRequest = {
      ...applicantRequest,
      applicantId,
      cif,
      primary,
      firstName,
      middleName,
      lastName,
      suffix,
      nickname: preferredName,
      ssn,
      email,
      phoneNumber,
      addresses,
      isKyc,
      isEnrolledOLB,
      isExistingCustomer,
      isInGoodStanding,
      returningUser,
      annualIncome,
      primarySourceOfFunds,
      occupation,
      employerCountry
    };

    if (dateOfBirth) {
      applicantRequest.dateOfBirth = dateOfBirth;
    }

    if (isUSCitizen !== undefined) {
      (applicantRequest as any).isUSCitizen = isUSCitizen;
      (applicantRequest as any).citizenship = isUSCitizen === false ? citizenship : "";
    }

    return applicantRequest;
  }
}

